import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { UserInfo, UserConfig } from '../types';
import { useAuth } from '../hooks/useAuth';
import { usePayment } from '../hooks/usePayment';

interface AppContextType {
  // User data
  userInfo: UserInfo | null;
  userConfig: UserConfig | null;
  
  // Auth state
  error: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isPaymentLoading: boolean;
  
  // Actions
  updateUserInfo: (userInfo: UserInfo) => void;
  updateUserConfig: (userConfig: UserConfig) => void;
  completeOnboarding: () => Promise<void>;
  handlePremiumClick: () => Promise<void>;
  handleBalanceUpdate: (newBalance: number) => void;
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Auth hook provides user data and auth state
  const { 
    userInfo, 
    userConfig, 
    error, 
    isLoading, 
    isAuthenticated,
    completeOnboarding, 
    updateUserInfo,
    updateUserConfig 
  } = useAuth();

  // Payment integration
  const { isLoading: isPaymentLoading, handlePremiumClick } = usePayment({
    onPaymentComplete: (updatedUserInfo: UserInfo) => {
      updateUserInfo(updatedUserInfo);
    }
  });

  // Handle balance updates
  const handleBalanceUpdate = useCallback((newBalance: number) => {
    if (userInfo) {
      updateUserInfo({ ...userInfo, balance: newBalance });
    }
  }, [userInfo, updateUserInfo]);

  // Create stable context value
  const contextValue: AppContextType = {
    // User data
    userInfo,
    userConfig,
    
    // Auth state
    error,
    isLoading,
    isAuthenticated,
    isPaymentLoading,
    
    // Actions
    updateUserInfo,
    updateUserConfig,
    completeOnboarding,
    handlePremiumClick,
    handleBalanceUpdate
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};